/**
 * Shared cookie consent state — granular per-category consent for ePrivacy compliance.
 *
 * Categories:
 *  - essential: always true (session cookies, auth, preferences)
 *  - analytics: PostHog (opt-in required)
 *  - errorTracking: Sentry (opt-in required)
 *
 * Stripe is categorized as "essential for payment" — loads only on payment pages,
 * no separate consent toggle needed (D-18).
 */

export type CookieConsentCategories = {
  essential: true; // always true, not toggleable
  analytics: boolean; // PostHog
  errorTracking: boolean; // Sentry
};

const COOKIE_CONSENT_KEY = "cookie-consent-categories";
const LEGACY_CONSENT_KEY = "cookie-consent";

/**
 * Same-tab notification fired whenever cookie consent categories are written or
 * cleared. Consumers (PostHog opt-in, the Affonso affiliate pixel, Sentry) key
 * off this single event so one consent decision governs every gated tag.
 * `storage` events do NOT fire in the same tab, hence a CustomEvent.
 */
export const COOKIE_CONSENT_CHANGED_EVENT = "alchema:cookie-consent-changed";

/** Broadcast a consent change to same-tab listeners. */
export function dispatchCookieConsentChanged(
  categories: CookieConsentCategories | null,
): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent(COOKIE_CONSENT_CHANGED_EVENT, { detail: categories }),
  );
}

/**
 * Read current cookie consent categories from localStorage.
 * Handles migration from legacy binary consent format.
 * Returns null if no consent has been given yet.
 */
export function getCookieConsent(): CookieConsentCategories | null {
  if (typeof window === "undefined") return null;

  // Check for new granular consent first
  const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
  if (stored) {
    try {
      return JSON.parse(stored) as CookieConsentCategories;
    } catch {
      return null;
    }
  }

  // Migrate legacy binary consent
  const legacy = localStorage.getItem(LEGACY_CONSENT_KEY);
  if (legacy === "accepted") {
    const migrated: CookieConsentCategories = {
      essential: true,
      analytics: true,
      errorTracking: true,
    };
    setCookieConsent(migrated);
    localStorage.removeItem(LEGACY_CONSENT_KEY);
    return migrated;
  }
  if (legacy === "declined") {
    const migrated: CookieConsentCategories = {
      essential: true,
      analytics: false,
      errorTracking: false,
    };
    setCookieConsent(migrated);
    localStorage.removeItem(LEGACY_CONSENT_KEY);
    return migrated;
  }

  return null;
}

/**
 * Persist cookie consent categories to localStorage.
 */
export function setCookieConsent(categories: CookieConsentCategories): void {
  if (typeof window === "undefined") return;
  localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(categories));
  dispatchCookieConsentChanged(categories);
}

/**
 * Clear all cookie consent state. Used by resetCookieConsent().
 */
export function clearCookieConsent(): void {
  if (typeof window === "undefined") return;
  localStorage.removeItem(COOKIE_CONSENT_KEY);
  localStorage.removeItem(LEGACY_CONSENT_KEY);
  dispatchCookieConsentChanged(null);
}
